import React from "react";
import { useQuery } from "@tanstack/react-query";
import { http, fmtGBP } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { ChartBar, TrendUp, Wrench, Tag } from "@phosphor-icons/react";

function Bar({ value, max, color = "#002FA7" }) {
  const pct = max ? Math.max(2, Math.round((value / max) * 100)) : 0;
  return <div className="h-1.5 bg-slate-100"><div className="h-full transition-all" style={{ width: `${pct}%`, background: color }} /></div>;
}

export default function Reports() {
  const { data: s } = useQuery({ queryKey: ["summary"], queryFn: () => http.get("/reports/summary").then(r => r.data) });
  const { data: spend } = useQuery({ queryKey: ["spend"], queryFn: () => http.get("/reports/spend").then(r => r.data) });
  if (!s) return <div className="p-8 text-sm text-slate-500">Loading…</div>;

  const totalStatusCount = Object.values(s.by_status || {}).reduce((a, b) => a + b, 0) || 1;
  const maxStoreSpend = Math.max(1, ...(s.spend_by_store || []).map(x => x.spend));
  const maxCatSpend = Math.max(1, ...(spend?.spend_by_category || []).map(x => x.spend));
  const maxTechSpend = Math.max(1, ...(spend?.spend_by_technician || []).map(x => x.spend));
  const maxMonthSpend = Math.max(1, ...(spend?.monthly_trend || []).map(x => x.spend));

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="overline">Analytics</div>
      <h1 className="text-3xl font-heading font-semibold tracking-tight mb-6 flex items-center gap-3"><ChartBar size={26} /> Reports</h1>

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="rmms-card p-5" data-testid="report-kpi-total"><div className="overline">Total jobs</div><div className="text-3xl font-mono-data mt-2 tabular-nums">{s.total_jobs}</div></div>
        <div className="rmms-card p-5" data-testid="report-kpi-completed"><div className="overline">Completed</div><div className="text-3xl font-mono-data mt-2 tabular-nums">{s.completed_count}</div></div>
        <div className="rmms-card p-5" data-testid="report-kpi-overdue"><div className="overline">Overdue</div><div className="text-3xl font-mono-data mt-2 tabular-nums">{s.overdue}</div></div>
        <div className="rmms-card p-5" data-testid="report-kpi-spend"><div className="overline">Spend MTD</div><div className="text-3xl font-mono-data mt-2 tabular-nums">{fmtGBP(s.spend_mtd)}</div></div>
      </div>

      {/* Monthly trend full width */}
      <div className="rmms-card p-5 mb-4">
        <div className="overline mb-4 flex items-center gap-1.5"><TrendUp size={11} /> Spend trend · last 6 months</div>
        <div className="grid grid-cols-6 gap-3 items-end h-40">
          {(spend?.monthly_trend || []).map(m => {
            const h = m.spend > 0 ? Math.max(8, Math.round((m.spend / maxMonthSpend) * 100)) : 4;
            return (
              <div key={m.month} data-testid={`monthly-${m.month}`} className="flex flex-col items-center justify-end h-full">
                <div className="font-mono-data text-[10px] tabular-nums mb-1 text-slate-600">{fmtGBP(m.spend)}</div>
                <div className="w-full transition-all" style={{ height: `${h}%`, background: "#002FA7", minHeight: "4px" }} />
                <div className="text-[10px] uppercase tracking-wider font-heading text-slate-500 mt-2">{m.label}</div>
              </div>
            );
          })}
          {!spend && <div className="col-span-6 text-sm text-slate-400">Loading…</div>}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <div className="rmms-card p-5">
          <div className="overline mb-3">Jobs by status</div>
          <div className="space-y-3">
            {Object.entries(s.by_status || {}).map(([k, v]) => (
              <div key={k} data-testid={`report-status-${k}`}>
                <div className="flex justify-between mb-1.5"><StatusBadge status={k} /><span className="font-mono-data text-sm tabular-nums">{v}</span></div>
                <Bar value={v} max={totalStatusCount} />
              </div>
            ))}
          </div>
        </div>

        <div className="rmms-card p-5">
          <div className="overline mb-3">Spend by store</div>
          <div className="space-y-3">
            {(s.spend_by_store || []).map(x => (
              <div key={x.store_id} data-testid={`report-spend-store-${x.store_id}`}>
                <div className="flex justify-between mb-1.5"><span className="text-sm">{x.store_name}</span><span className="font-mono-data text-sm tabular-nums">{fmtGBP(x.spend)}</span></div>
                <Bar value={x.spend} max={maxStoreSpend} color="#10B981" />
              </div>
            ))}
            {(!s.spend_by_store || s.spend_by_store.length === 0) && <div className="text-sm text-slate-400">No spend yet.</div>}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rmms-card p-5">
          <div className="overline mb-3 flex items-center gap-1.5"><Tag size={11} /> Spend by category</div>
          <div className="space-y-3">
            {(spend?.spend_by_category || []).map(x => (
              <div key={x.category_id} data-testid={`report-spend-cat-${x.category_id}`}>
                <div className="flex justify-between mb-1.5"><span className="text-sm">{x.category_name}</span><span className="font-mono-data text-sm tabular-nums">{fmtGBP(x.spend)}</span></div>
                <Bar value={x.spend} max={maxCatSpend} color="#F59E0B" />
              </div>
            ))}
            {(!spend?.spend_by_category || spend.spend_by_category.length === 0) && <div className="text-sm text-slate-400">No spend yet.</div>}
          </div>
        </div>

        <div className="rmms-card p-5">
          <div className="overline mb-3 flex items-center gap-1.5"><Wrench size={11} /> Spend by technician</div>
          <div className="space-y-3">
            {(spend?.spend_by_technician || []).map(x => (
              <div key={x.technician_id} data-testid={`report-spend-tech-${x.technician_id}`}>
                <div className="flex justify-between mb-1.5">
                  <span className="text-sm">{x.technician_name} <span className="text-[11px] text-slate-500">· {x.jobs} job{x.jobs === 1 ? "" : "s"}</span></span>
                  <span className="font-mono-data text-sm tabular-nums">{fmtGBP(x.spend)}</span>
                </div>
                <Bar value={x.spend} max={maxTechSpend} color="#6366F1" />
              </div>
            ))}
            {(!spend?.spend_by_technician || spend.spend_by_technician.length === 0) && <div className="text-sm text-slate-400">No technician spend yet.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
